// src/components/GiftSetsPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import styled, { keyframes, css } from "styled-components";
import { FaChevronDown, FaChevronUp, FaFilter, FaSort, FaTimes } from "react-icons/fa";
import { ShoppingCart } from "lucide-react";
import FavoriteButton from "./FavoriteButton";
import { useLanguage } from "./LanguageContext";
import { useDataRefresh } from "./liveUpdates/DataRefreshProvider";
import { api } from "../lib/http";

const API_BASE_URL = "https://shopaholics.ge/api/gift_sets";

// ================= THEME =================
const COLORS = {
  text: "#111827",
  dim: "#6b7280",
  line: "rgba(0,0,0,.08)",
  subtle: "#f7f7f8",
  bg: "#ffffff",
  brand: "#a15a5a",
  brandDark: "#8e4a4a",
  card: "#fff",
  shadow: "0 10px 30px rgba(0,0,0,.08)",
};

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 12px clamp(10px, 4vw, 16px) 28px;
  overflow-x: hidden;
`;
const GridWrap = styled(Container)`
  padding-top: 18px;
  overflow-x: hidden;
  padding-left: 0;
  padding-right: 0;
  @media (min-width: 641px) { padding-left: 16px; padding-right: 16px; }
`;

// ================= LOADER =================
const spin = keyframes`to { transform: rotate(360deg); }`;
const PageLoader = styled.div` display: grid; place-items: center; min-height: 60vh; `;
const Spinner = styled.div`
  width: 42px; height: 42px; border-radius: 50%;
  border: 4px solid rgba(0,0,0,.1); border-top-color: ${COLORS.text};
  animation: ${spin} 1s linear infinite;
`;

// ================= TOP BAR =================
const TopBar = styled.div`
  position: sticky;
  top: 86px;
  z-index: 5;
  background: rgba(255,255,255,.8);
  backdrop-filter: saturate(180%) blur(8px);
  border-bottom: 1px solid ${COLORS.line};
  @media (max-width: 900px) { top: 150px; }
`;
const TopBarInner = styled(Container)`
  display: flex; align-items: center; gap: 10px; justify-content: space-between;
  padding-top: 10px; padding-bottom: 10px; overflow: visible;
`;
const LeftActions = styled.div` display: flex; gap: 10px; flex-wrap: wrap; `;
const RightActions = styled.div` position: relative; `;
const PillButton = styled.button`
  appearance: none; border: 1px solid ${COLORS.line};
  background: #fff; color: ${COLORS.text};
  border-radius: 999px; height: 40px; padding: 0 14px;
  display: inline-flex; align-items: center; gap: 8px;
  font-weight: 700; cursor: pointer;
  transition: box-shadow .15s ease, transform .15s ease, border-color .15s ease;
  &:hover { box-shadow: ${COLORS.shadow}; transform: translateY(-1px); }
`;
const SortDropdownWrap = styled.div` position: relative; `;
const DropdownCard = styled.div`
  position: absolute; top: calc(100% + 8px); right: 0;
  min-width: 220px; background: #fff; border: 1px solid ${COLORS.line};
  border-radius: 14px; box-shadow: ${COLORS.shadow}; padding: 8px; z-index: 20;
  ${({ $hidden }) => $hidden && css`display:none;`}
`;
const DropdownItem = styled.button`
  width: 100%; text-align: left; border: 0; background: transparent;
  padding: 10px 12px; border-radius: 10px; font-weight: 600; cursor: pointer;
  &:hover { background: ${COLORS.subtle}; }
`;

// ================= APPLIED FILTERS =================
const AppliedFiltersBox = styled(Container)`
  padding-top: 10px; display: flex; flex-wrap: wrap; gap: 8px; align-items: center;
`;
const FilterTag = styled.div`
  display: inline-flex; align-items: center; gap: 8px;
  background: #f0f0f1; color: ${COLORS.text};
  border: 1px solid ${COLORS.line}; border-radius: 999px;
  padding: 6px 10px; font-size: 13px;
  .close { display: inline-flex; align-items: center; cursor: pointer; opacity: .7; }
  .close:hover { opacity: 1; }
`;

// ================= Drawer =================
const Overlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,.35);
  z-index: 3005; opacity: ${({ $open }) => ($open ? 1 : 0)};
  visibility: ${({ $open }) => ($open ? "visible" : "hidden")}; transition: .2s;
`;
const FiltersMenu = styled.aside`
  position: fixed; top: 0; left: 0; height: 100dvh; width: 86vw; max-width: 360px;
  background: #ffffff; border-right: 1px solid rgba(0,0,0,.08);
  transform: translateX(${({ isVisible }) => (isVisible ? "0" : "-100%")});
  transition: transform .25s ease; z-index: 3006;
  display: grid; grid-template-rows: auto 1fr auto; overflow: hidden; overflow-y: auto;
  -webkit-overflow-scrolling: touch; overscroll-behavior: contain;
`;
const FilterTop = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 16px; border-bottom: 1px solid rgba(0,0,0,.08);
  h3 { margin: 0; font-size: 16px; }
`;
const CloseButton = styled.button`
  background: #fff; border: 1px solid rgba(0,0,0,.08);
  border-radius: 10px; height: 36px; width: 36px;
  display: grid; place-items: center; cursor: pointer;
`;
const FilterBody = styled.div`
  padding: 12px 14px 18px; display: grid; gap: 14px;
  min-height: 0; overflow-y: auto; -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain; overflow-x: hidden;
`;
const FilterSection = styled.div` border: 1px solid rgba(0,0,0,.08); border-radius: 12px; background: #fff; `;
const FilterHeaderBtn = styled.button`
  width: 100%; border: 0; background: #fff; padding: 14px;
  display: flex; align-items: center; justify-content: space-between;
  font-weight: 800; cursor: pointer; user-select: none;
`;
const FilterOptions = styled.div`
  display: flex; flex-direction: column; overflow: hidden;
  max-height: ${({ isExpanded, $tall }) => (isExpanded ? ($tall ? "68vh" : "52vh") : "0")};
  pointer-events: ${({ isExpanded }) => (isExpanded ? "auto" : "none")};
  transition: max-height .25s ease;
  > .scroll { flex: 1; min-height: 0; overflow-y: auto; padding: 8px 12px 12px;
    -webkit-overflow-scrolling: touch; overscroll-behavior: contain; touch-action: pan-y;
    scrollbar-width: thin;
  }
  > .scroll::-webkit-scrollbar { width: 8px; }
  > .scroll::-webkit-scrollbar-thumb { background: #a15a5a; border-radius: 8px; }
  @media (max-width: 768px) { max-height: ${({ isExpanded }) => (isExpanded ? "60vh" : "0")}; }
`;

const CheckboxItem = styled.label`
  display: grid; grid-template-columns: 22px 1fr; align-items:center; gap: 10px; cursor: pointer; font-size: 14px;
  input{ display:none; }
  input + span{ width: 20px; height: 20px; border:1px solid #cfcfcf; border-radius: 4px; background: #fff; display:inline-block; position:relative; }
  input:checked + span{ background: ${COLORS.brand}; border-color: ${COLORS.brand}; }
  input:checked + span::after{ content:''; position:absolute; inset: 4px; border: 2px solid #fff; border-top: 0; border-left: 0; transform: rotate(45deg); }
`;

const PriceFilterContainer = styled.div` background:#fafafa; border:1px solid ${COLORS.line}; border-radius: 10px; padding: 12px; display:grid; gap:12px; `;
const PriceRangeWrapper = styled.div` display:grid; gap:6px; `;
const PriceLabel = styled.label` font-weight:800; font-size: 14px; display:flex; justify-content:space-between; color:${COLORS.text}; `;
const PriceRangeInput = styled.input`
  width: 100%; height: 6px; border-radius: 999px; outline: none; -webkit-appearance: none;
  background: linear-gradient(to right, ${COLORS.brand} 0%, #e7c2c2 100%);
  &::-webkit-slider-thumb{ appearance:none; width:18px; height:18px; border-radius: 50%; background: ${COLORS.brand}; cursor:pointer; border: 2px solid #fff; box-shadow: 0 2px 6px rgba(0,0,0,.2); }
`;
const DrawerFooter = styled.div` border-top: 1px solid ${COLORS.line}; padding: 12px; display: grid; gap: 8px; `;
const ClearFiltersButton = styled.button`
  width: 100%; height: 44px; border-radius: 12px; border: 1px solid ${COLORS.line};
  background: ${COLORS.text}; color:#fff; font-weight:800; cursor:pointer;
  transition: filter .15s ease, transform .15s ease;
  &:hover{ filter: brightness(.95); transform: translateY(-1px); }
`;

// ================= GRID / CARDS =================
const PerfumeGrid = styled.div`
  display: grid; gap: 16px; grid-template-columns: repeat(5, minmax(0, 1fr));
  @media (max-width: 1200px) { grid-template-columns: repeat(4, minmax(0, 1fr)); gap:14px; }
  @media (max-width: 900px)  { grid-template-columns: repeat(3, minmax(0, 1fr)); gap:12px; }
  @media (max-width: 640px)  { grid-template-columns: repeat(2, minmax(0, 1fr)); gap:10px; }
  @media (max-width: 380px)  { grid-template-columns: repeat(2, minmax(0, 1fr)); gap:8px; }
`;
const PerfumeCard = styled.div`
  background: ${COLORS.card}; border: 1px solid ${COLORS.line}; border-radius: 16px; padding: 12px;
  position: relative; transition: box-shadow .2s ease, transform .2s ease, border-color .2s ease;
  cursor: pointer; min-width: 0; box-sizing: border-box;
   &:hover { box-shadow: ${COLORS.shadow}; transform: translateY(-2px); border-color: rgba(0,0,0,.12); }
  .hover-options {
    display: ${({ $show }) => ($show ? 'flex' : 'none')};
    position: absolute; inset: 0;
    background: rgba(255,255,255,.6); backdrop-filter: blur(6px);
    align-items: center; justify-content: center; z-index: 2; border-radius: 16px;
  }
  .view-btn{ border: 1px solid ${COLORS.line}; background:#fff; border-radius: 12px; height: 38px; padding: 0 12px; font-weight: 800; cursor: pointer;
    transition: transform .15s ease, box-shadow .15s ease; color: black; }
  .view-btn:hover{ transform: translateY(-1px); box-shadow: ${COLORS.shadow}; }
  @media (max-width: 640px) { padding: 8px; border-radius: 12px; }
  @media (max-width: 380px) { padding: 6px; border-radius: 10px; }
`;
const blink = keyframes` 0%{opacity:1} 50%{opacity:.2} 100%{opacity:1} `;
const SaleBadge = styled.div`
  position: absolute; top: 10px; right: 10px; background: #ef4444; color: #fff; font-weight: 800;
  padding: 6px 10px; border-radius: 999px; font-size: 11px; box-shadow: 0 6px 16px rgba(239,68,68,.35);
  animation: ${blink} 1.5s infinite; z-index: 100;
  @media (max-width: 640px) { top: 8px; right: 8px; font-size: 10px; padding: 5px 8px; }
`;
const ImgWrap = styled.div`
  width: 100%; aspect-ratio: 1 / 1; border-radius: 12px; background: ${COLORS.subtle};
  display: grid; place-items: center; overflow: hidden; margin-bottom: 10px; position: relative;
  @media (max-width: 640px) { margin-bottom: 6px; }
`;
const PerfumeImage = styled.img`
  width: 80%; height: 80%; object-fit: contain; object-position: center; display: block;
  @media (max-width: 640px) { width: 80%; height: 80%; }
  @media (max-width: 380px) { width: 80%; height: 80%; }
`;
const PerfumeName = styled.h3`
  font-size: 14px; font-weight: 800; color: ${COLORS.text}; margin: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  @media (max-width: 640px) { font-size: 13px; }
  @media (max-width: 380px) { font-size: 12.5px; }
`;
const PerfumeBrand = styled.div`
  font-size: 12px; color: ${COLORS.dim}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  @media (max-width: 640px) { font-size: 11px; }
`;
const PriceBlock = styled.div`
  margin-top: 8px; font-weight: 800; color: ${COLORS.text}; font-size: 13px;
  .old { color:#9ca3af; text-decoration: line-through; margin-right: 6px; }
  @media (max-width: 640px) { font-size: 12px; }
`;
const IconBar = styled.div`
  position: absolute; left: 10px; top: 10px; display: flex; gap: 8px; z-index: 3;
  .icon-btn{
    width: 36px; height: 36px; border-radius: 10px; border: 1px solid ${COLORS.line};
    background:#fff; display:grid; place-items:center; cursor:pointer;
    transition: transform .15s ease, box-shadow .15s ease;
  }
  .icon-btn:hover{ transform: translateY(-1px); box-shadow: ${COLORS.shadow}; }
`;
const LoadMoreButton = styled.button`
  display: block; margin: 24px auto 40px; height: 44px; padding: 0 16px;
  background: ${COLORS.text}; color:#fff; border: 0; border-radius: 12px; font-weight: 800; cursor:pointer;
  transition: transform .15s ease, filter .15s ease;
  &:hover{ transform: translateY(-1px); filter: brightness(.95); }
`;

// ================= HELPERS =================
const ucFirst = (s = "") => {
  const trimmed = String(s).trim();
  if (!trimmed) return "";
  return trimmed.charAt(0).toLocaleUpperCase() + trimmed.slice(1);
};


// ================= COMPONENT =================
export default function GiftSetsPage({ addToCart }) {
  const { version } = useDataRefresh();          // cache-bust: ?v=version
  const { t, language } = useLanguage();

  // server-driven list
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [silentLoading, setSilentLoading] = useState(false);

  // paging
  const PAGE_SIZE = 40;
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  // filters/sort
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [sortDropdownVisible, setSortDropdownVisible] = useState(false);
  const [sortOrder, setSortOrder] = useState(""); // "asc" | "desc" | ""
  const [selectedGiftCategories, setSelectedGiftCategories] = useState([]); // uses gift_sets.subcategory
  const [selectedGenders, setSelectedGenders] = useState([]);               // "man","woman","kids","unisex"

  // price filter (server-side)
  const [minPrice, setMinPrice] = useState(0);  // UI bounds (derived from loaded results)
  const [maxPrice, setMaxPrice] = useState(0);
  const [priceRange, setPriceRange] = useState([0,0]);
  const [isPriceFilterActive, setIsPriceFilterActive] = useState(false);

  // local derived categories from returned items (unless გაქვს ცალკე meta endpoint)
  const [giftCategories, setGiftCategories] = useState([]);

  const dropdownRef = useRef(null);
  const filterMenuRef = useRef(null);
    const didMountRef = useRef(false);

  const [activeCardId, setActiveCardId] = useState(null);
  const toggleCard = (id) => setActiveCardId(prev => (prev === id ? null : id));

  useEffect(() => { document.title = t("titles.category"); }, [language]);

  // ===== Helpers to build query for the backend page =====
  const buildQuery = (custom = {}) => {
    const params = new URLSearchParams();

    // filters
    if (selectedGenders.length) params.set("sex", selectedGenders.join(","));
    if (selectedGiftCategories.length) params.set("category", selectedGiftCategories.join(","));
    if (isPriceFilterActive) {
      params.set("price_min", String(priceRange[0]));
      params.set("price_max", String(priceRange[1]));
    }

    // sort
    if (sortOrder === "asc") params.set("sort", "price_asc");
    if (sortOrder === "desc") params.set("sort", "price_desc");

    // paging
    params.set("limit", String(custom.limit ?? PAGE_SIZE));
    params.set("offset", String(custom.offset ?? 0));

    // version (cache-bust / live refresh semantics)
    params.set("v", String(version));

    return params.toString();
  };

  // ===== Initial + filter-driven load (resets list) =====
  const fetchFirstPage = async ({ silent = false } = {}) => {
    if (silent) setSilentLoading(true); else setLoading(true);
    try {
      const qs = buildQuery({ offset: 0, limit: PAGE_SIZE });
      const res = await api.get(`${API_BASE_URL}/gift_set_page.php?${qs}`);
      const list = res?.data?.items || [];
      const count = res?.data?.total ?? list.length;

      setItems(list);
      setTotal(count);
      setOffset(list.length);
      setHasMore(list.length < count);

      // derive categories from current batch
      const cats = [...new Set(list.map(x => (x.subcategory || "").trim()).filter(Boolean))].sort((a,b)=>a.localeCompare(b));
      setGiftCategories(cats);

      // derive price bounds from current batch (if server does not return separate meta)
      const prices = list.map(x => Number(x.final_price ?? x.compare_price ?? 0)).filter(n => isFinite(n) && n>0);
      const minP = prices.length ? Math.min(...prices) : 0;
      const maxP = prices.length ? Math.max(...prices) : 0;
      setMinPrice(minP); setMaxPrice(maxP);
      if (!isPriceFilterActive) setPriceRange([minP, maxP]); // არ გადააწერო, თუ ενεργოა
    } catch (e) {
      console.error("gift_set_page first load error:", e);
      setItems([]); setTotal(0); setOffset(0); setHasMore(false);
    } finally {
      if (silent) setSilentLoading(false); else setLoading(false);
    }
  };

  // run on mount + whenever filters/sort/version change
  // initial load (full loader)
  useEffect(() => {
    (async () => {
      await fetchFirstPage();       // full loader
      didMountRef.current = true;
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // subsequent changes => silent refresh (no page flicker)
  useEffect(() => {
    if (!didMountRef.current) return;
    fetchFirstPage({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, sortOrder, JSON.stringify(selectedGiftCategories), JSON.stringify(selectedGenders), isPriceFilterActive, priceRange[0], priceRange[1]]);

  // ===== Load more (append) =====
  const loadMore = async () => {
    if (!hasMore || silentLoading) return;
    setSilentLoading(true);
    try {
      const qs = buildQuery({ offset, limit: PAGE_SIZE });
      const res = await api.get(`${API_BASE_URL}/gift_set_page.php?${qs}`);
      const list = res?.data?.items || [];
      const count = res?.data?.total ?? total;

      setItems(prev => [...prev, ...list]);
      setOffset(prev => prev + list.length);
      setTotal(count);
      setHasMore(offset + list.length < count);

      // უახლესი batch-იდან კატეგორიების გაფართოება (თუ ახალი გამოჩნდა)
      if (list.length) {
        const newCats = [...new Set(list.map(x => (x.subcategory || "").trim()).filter(Boolean))];
        setGiftCategories(prev => {
          const merged = new Set([...prev, ...newCats]);
          return [...merged].sort((a,b)=>a.localeCompare(b));
        });
        // ფასის UI დიაპაზონის განახლება საერთო ნანახ სეტზე
        const allPrices = [...items, ...list].map(x => Number(x.final_price ?? x.compare_price ?? 0)).filter(n => isFinite(n) && n>0);
        if (allPrices.length) {
          const minP = Math.min(...allPrices);
          const maxP = Math.max(...allPrices);
          setMinPrice(minP); setMaxPrice(maxP);
          if (!isPriceFilterActive) setPriceRange([minP, maxP]);
        }
      }
    } catch (e) {
      console.error("gift_set_page load more error:", e);
    } finally {
      setSilentLoading(false);
    }
  };

  // ===== UI Handlers =====
  const toggleFiltersVisibility = () => setFiltersVisible(p => !p);
  const toggleSection = (setter, val) => setter(!val);
  const [categoryExpanded, setCategoryExpanded] = useState(true);
  const [genderExpanded, setGenderExpanded] = useState(true);
  const [priceExpanded, setPriceExpanded] = useState(true);

  const handlePriceChange = (e) => {
    const value = Number(e.target.value);
    setIsPriceFilterActive(true);
    if (e.target.name === "min") setPriceRange([value, priceRange[1]]);
    else setPriceRange([priceRange[0], value]);
  };

  // Chips
  const appliedFilters = useMemo(() => {
    const chips = [
      ...selectedGiftCategories.map((v) => ({ type: "giftcat", value: v })),
      ...selectedGenders.map((v) => ({ type: "gender", value: v })),
      sortOrder && { type: "sort", value: sortOrder === "asc" ? "Min to Max" : "Max to Min" },
      isPriceFilterActive && { type: "price" }
    ].filter(Boolean);
    return chips;
  }, [selectedGiftCategories, selectedGenders, sortOrder, isPriceFilterActive, priceRange]);

  const removeFilter = (filter) => {
    if (filter.type === "giftcat") setSelectedGiftCategories(prev => prev.filter(x => x !== filter.value));
    else if (filter.type === "gender") setSelectedGenders(prev => prev.filter(x => x !== filter.value));
    else if (filter.type === "sort") setSortOrder("");
    else if (filter.type === "price") { setIsPriceFilterActive(false); setPriceRange([minPrice, maxPrice]); }
  };

  const clearFilters = () => {
    setSelectedGiftCategories([]);
    setSelectedGenders([]);
    setSortOrder("");
    setIsPriceFilterActive(false);
    setPriceRange([minPrice, maxPrice]);  // reset to current bounds from data
    setOffset(0);
    fetchFirstPage();
  };

  // outside click close
  useEffect(() => {
    const handleClickOutside = (event) => {
      // დახურე Filters/Sort
      if (filterMenuRef.current && !filterMenuRef.current.contains(event.target)) setFiltersVisible(false);
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setSortDropdownVisible(false);

      // თუ activeCardId ჩართულია და დაჭერა მოხდა არა ამ ბარათში — გამორთე
      if (activeCardId != null) {
        const el = event.target.closest('[data-gift-card]');
        const clickedId = el?.getAttribute('data-id');
        if (String(clickedId) !== String(activeCardId)) setActiveCardId(null);
      }
    };
    const handleEsc = (e) => { if (e.key === 'Escape') setActiveCardId(null); };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [activeCardId]);

  if (loading) return <PageLoader><Spinner/></PageLoader>;

  return (
    <>
      {/* ===== Sticky Top Bar ===== */}
      <TopBar>
        <TopBarInner>
          <LeftActions>
            <PillButton onClick={toggleFiltersVisibility}><FaFilter/>{t("categorypage.filter")}</PillButton>
          </LeftActions>

          <RightActions ref={dropdownRef}>
            <SortDropdownWrap>
              <PillButton onClick={() => setSortDropdownVisible(p => !p)}><FaSort/>{t("categorypage.sort")}</PillButton>
              <DropdownCard $hidden={!sortDropdownVisible}>
                <DropdownItem onClick={() => { setSortOrder('asc'); setSortDropdownVisible(false); }}>
                  {t("categorypage.mintomax")}
                </DropdownItem>
                <DropdownItem onClick={() => { setSortOrder('desc'); setSortDropdownVisible(false); }}>
                  {t("categorypage.maxtomin")}
                </DropdownItem>
              </DropdownCard>
            </SortDropdownWrap>
          </RightActions>
        </TopBarInner>

        {/* Applied filters chips */}
        <AppliedFiltersBox>
          {appliedFilters.length ? (
            appliedFilters.map((f, i) => (
              <FilterTag key={i}>
                {f.type === "giftcat" ? `${t("categorypage.categories")}:`
                 : f.type === "gender" ? `${t("categorypage.gender")}:`
                 : f.type === "sort" ? `${t("categorypage.sort-filter")}:`
                 : f.type === "price" ? `${t("categorypage.price")}: ${priceRange[0]} - ${priceRange[1]} ${t("newproducts.currency")}`
                 : f.type}{" "}
                {f.type === "gender" ? t(`categorypage.${f.value}`) : ucFirst(f.value || "")}
                <span className="close" onClick={() => removeFilter(f)}><FaTimes/></span>
              </FilterTag>
            ))
          ) : (
            <span style={{ color: COLORS.dim, fontSize: 13 }}>{t("categorypage.filteroff")}</span>
          )}
        </AppliedFiltersBox>
      </TopBar>

      {/* ===== Drawer ===== */}
      <Overlay $open={filtersVisible} onClick={() => setFiltersVisible(false)} />
      <FiltersMenu isVisible={filtersVisible} ref={filterMenuRef}>
        <FilterTop>
          <h3>{t("categorypage.filter")}</h3>
          <CloseButton onClick={() => setFiltersVisible(false)} aria-label="Close"><FaTimes/></CloseButton>
        </FilterTop>

        <FilterBody>
          {/* Gift categories (from subcategory) */}
          <FilterSection>
            <FilterHeaderBtn onClick={() => toggleSection(setCategoryExpanded, categoryExpanded)}>
              <span>{t("categorypage.categories")}</span>
              {categoryExpanded ? <FaChevronUp/> : <FaChevronDown/>}
            </FilterHeaderBtn>
            <FilterOptions isExpanded={categoryExpanded}>
              <div className="scroll">
                {giftCategories.map(cat => (
                  <CheckboxItem key={cat}>
                    <input
                      type="checkbox"
                      value={cat}
                      checked={selectedGiftCategories.includes(cat)}
                      onChange={(e) =>
                        setSelectedGiftCategories(prev => e.target.checked
                          ? [...prev, cat]
                          : prev.filter(c => c !== cat)
                        )
                      }
                    />
                    <span></span>{ucFirst(t(`categorypage.${cat}`) || cat)}
                  </CheckboxItem>
                ))}
              </div>
            </FilterOptions>
          </FilterSection>

          {/* Gender */}
          <FilterSection>
            <FilterHeaderBtn onClick={() => toggleSection(setGenderExpanded, genderExpanded)}>
              <span>{t("categorypage.gender")}</span>
              {genderExpanded ? <FaChevronUp/> : <FaChevronDown/>}
            </FilterHeaderBtn>
            <FilterOptions isExpanded={genderExpanded}>
              <div className="scroll">
                {["man", "woman", "kids", "unisex"].map((g) => (
                  <CheckboxItem key={g}>
                    <input
                      type="checkbox"
                      value={g}
                      checked={selectedGenders.includes(g)}
                      onChange={(e) =>
                        setSelectedGenders(prev => e.target.checked
                          ? [...prev, g]
                          : prev.filter(x => x !== g)
                        )
                      }
                    />
                    <span></span>{t(`categorypage.${g}`)}
                  </CheckboxItem>
                ))}
              </div>
            </FilterOptions>
          </FilterSection>

          {/* Price */}
          <FilterSection>
            <FilterHeaderBtn onClick={() => toggleSection(setPriceExpanded, priceExpanded)}>
              <span>{t("categorypage.price")}</span>
              {priceExpanded ? <FaChevronUp/> : <FaChevronDown/>}
            </FilterHeaderBtn>
            <FilterOptions isExpanded={priceExpanded}>
              <div className="scroll" style={{ padding: 12 }}>
                <PriceFilterContainer>
                  <PriceRangeWrapper>
                    <PriceLabel>
                      {t("categorypage.minprice")} <b>{priceRange[0]} {t("newproducts.currency")}</b>
                    </PriceLabel>
                    <PriceRangeInput
                      type="range"
                      name="min"
                      min={minPrice}
                      max={maxPrice || 0}
                      value={priceRange[0]}
                      onChange={handlePriceChange}
                      onMouseUp={()=>setIsPriceFilterActive(true)}
                      onTouchEnd={()=>setIsPriceFilterActive(true)}
                    />
                  </PriceRangeWrapper>

                  <PriceRangeWrapper>
                    <PriceLabel>
                      {t("categorypage.maxprice")} <b>{priceRange[1]} {t("newproducts.currency")}</b>
                    </PriceLabel>
                    <PriceRangeInput
                      type="range"
                      name="max"
                      min={minPrice}
                      max={maxPrice || 0}
                      value={priceRange[1]}
                      onChange={handlePriceChange}
                      onMouseUp={()=>setIsPriceFilterActive(true)}
                      onTouchEnd={()=>setIsPriceFilterActive(true)}
                    />
                  </PriceRangeWrapper>
                </PriceFilterContainer>
              </div>
            </FilterOptions>
          </FilterSection>
        </FilterBody>

        <DrawerFooter>
          <ClearFiltersButton onClick={clearFilters}>{t("categorypage.clearfilters")}</ClearFiltersButton>
        </DrawerFooter>
      </FiltersMenu>

      {/* ===== GRID ===== */}
      <GridWrap>
        <PerfumeGrid>
          {items.map((item) => {
            const hasSale =
              item.final_price != null &&
              item.compare_price != null &&
              Number(item.final_price) < Number(item.compare_price);

            const title = language === "ka" ? (item.title_ka || item.title_en) : (item.title_en || item.title_ka);
            const percent = hasSale
              ? Math.round(((item.compare_price - item.final_price) / item.compare_price) * 100)
              : 0;

            return (
              <PerfumeCard 
                 key={item.id}
                 $show={activeCardId === item.id}
                onClick={() => toggleCard(item.id)}
                data-gift-card
                data-id={item.id}
            >
                {hasSale ? <SaleBadge>{percent}%</SaleBadge> : null}

                <div className="hover-options">
                  <Link to={`/gift-set/${item.gift_set_id}`} onClick={(e) => e.stopPropagation()}>
                    <button className="view-btn">{t("categorypage.viewmore")}</button>
                  </Link>
                </div>

                <ImgWrap>
                  <PerfumeImage src={item.image || ""} alt={title} />
                </ImgWrap>

                <PerfumeName title={title}>{title}</PerfumeName>
                {item.brand ? <PerfumeBrand title={item.brand}>{item.brand}</PerfumeBrand> : <div style={{height:14}} />}

                <PriceBlock>
                  {hasSale ? (
                    <>
                      <span className="old">{Number(item.compare_price)} {t("newproducts.currency")}</span>
                      <span>{Number(item.final_price)} {t("newproducts.currency")}</span>
                    </>
                  ) : (
                    <span>{Number(item.final_price ?? item.compare_price ?? 0)} {t("newproducts.currency")}</span>
                  )}
                </PriceBlock>

                <IconBar>
                  {Number(item.full_stock) >= 1 && (
                    <button
                      className="icon-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        addToCart({
                          type: "gift_set",
                          set_id: item.id,
                          gift_set_id: item.gift_set_id || null,
                          product_id: item.gift_set_id || null,
                                  id: item.gift_set_id || null,
                          name: title,
                          unit_price: Number(item.final_price || 0),
                          quantity: 1,
                          image: item.image || "",
                          full_stock: Number(item.full_stock || 0),
                        });
                        setActiveCardId(null);
                      }}
                      aria-label="Add to cart"
                      title={t("categorypage.addtocart") || "Add to cart"}
                    >
                      <ShoppingCart size={18} />
                    </button>
                  )}
                  <div className="icon-btn" onClick={(e) => e.stopPropagation()}>
                    <FavoriteButton productId={item.id} initialIsFavorite={item.is_favorite} />
                  </div>
                </IconBar>
              </PerfumeCard>
            );
          })}
        </PerfumeGrid>

        {hasMore && (
          <LoadMoreButton onClick={loadMore}>
            {silentLoading ? t("purchases.loading") : t("purchases.loadmore")}
          </LoadMoreButton>
        )}
      </GridWrap>
    </>
  );
}
